import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineArrowPath,
  HiOutlineDocumentArrowDown,
  HiOutlineQueueList,
  HiOutlineCheck,
} from "react-icons/hi2";
import { fiveSService } from "../services";
import { API_URL } from "../config/api";
import { toast } from "react-toastify";
import PageLoading from "../components/common/PageLoading";
import SharedModal from "../components/common/SharedModal";
import "./FiveS.css";

const FiveS = () => {
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklist, setChecklist] = useState({});
  const [newCategory, setNewCategory] = useState("");
  const [newQuestion, setNewQuestion] = useState({});

  const loadAudits = async () => {
    setLoading(true);
    try {
      const data = await fiveSService.getAllAudits();
      setAudits(data);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load audits.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudits();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this audit?")) {
      const toastId = toast.loading("Deleting audit...");
      try {
        await fiveSService.deleteAudit(id);
        await loadAudits();
        toast.update(toastId, { render: "Audit deleted successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
      } catch (error) {
        toast.update(toastId, { render: "Failed to delete audit.", type: "error", isLoading: false, autoClose: 3000, closeButton: true });
      }
    }
  };

  const loadImageForPdf = async (url) => {
    if (!url) return null;
    if (url.startsWith("data:")) return url;

    let fullUrl = url;
    if (!url.startsWith("http") && !url.startsWith("blob:")) {
      const cleanBase = API_URL.endsWith("/") ? API_URL.slice(0, -1) : API_URL;
      const cleanPath = url.startsWith("/") ? url : `/${url}`;
      fullUrl = `${cleanBase}${cleanPath}`;
    }

    const fetchAndConvert = async (fetchUrl) => {
      const response = await fetch(fetchUrl, { method: "GET", mode: "cors" });
      if (!response.ok) throw new Error(`Status ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);

      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            const maxWidth = 500;
            const maxHeight = 400;
            let width = img.width;
            let height = img.height;
            if (width > maxWidth || height > maxHeight) {
              const ratio = Math.min(maxWidth / width, maxHeight / height);
              width = width * ratio;
              height = height * ratio;
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            const base64 = canvas.toDataURL("image/jpeg", 0.8);
            URL.revokeObjectURL(objectUrl);
            resolve(base64);
          } catch (err) {
            URL.revokeObjectURL(objectUrl);
            reject(err);
          }
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          reject(new Error("Image Load Failed"));
        };
        img.src = objectUrl;
      });
    };

    try {
      return await fetchAndConvert(fullUrl);
    } catch (err) {
      return null;
    }
  };

  const generatePdf = async (summaryAudit) => {
    try {
      const audit = await fiveSService.getAuditById(summaryAudit.id);
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.setTextColor(40, 44, 52);
      doc.text("5S Audit Report", 14, 22);

      doc.setFontSize(12);
      doc.setTextColor(100);
      const dateStr = audit.auditDate ? new Date(audit.auditDate).toLocaleDateString() : "N/A";
      doc.text(`Date: ${dateStr}`, 14, 32);
      doc.text(`Auditor: ${audit.auditor || "N/A"}`, 14, 38);

      const plantName = audit.plant?.plantName || summaryAudit.plant?.plantName || "N/A";
      const areaName = audit.area?.areaName || summaryAudit.area?.areaName || "N/A";

      doc.text(`Plant: ${plantName}`, 100, 32);
      doc.text(`Area: ${areaName}`, 100, 38);
      doc.text(`Total Score: ${audit.totalScore || 0}`, 14, 46);

      if (audit.notes) doc.text(`Notes: ${audit.notes}`, 14, 54);

      const checklistItems = audit.items || audit.checklistItems || [];
      const tableData = checklistItems.map((item) => [
        item.category || item.Category || "",
        item.question || item.Question || "",
        item.score ?? item.Score ?? "-",
        item.remarks || item.Remarks || "",
      ]);

      let startY = audit.notes ? 60 : 50;

      autoTable(doc, {
        startY: startY,
        head: [["Category", "Question", "Score", "Remarks"]],
        body: tableData,
        theme: "grid",
        headStyles: { fillColor: [41, 128, 185], textColor: 255 },
        styles: { fontSize: 10, cellPadding: 3 },
        columnStyles: { 0: { cellWidth: 30 }, 1: { cellWidth: 70 }, 2: { cellWidth: 25, halign: "center" } },
      });

      doc.save(`5S_Audit_${dateStr.replace(/\//g, "-")}_${audit.id}.pdf`);
    } catch (error) {
      toast.error("Failed to generate PDF.");
    }
  };

  const openChecklistModal = async () => {
    try {
      const data = await fiveSService.getChecklistMaster();
      setChecklist(data);
      setShowChecklistModal(true);
    } catch (error) {
      toast.error("Failed to load checklist master.");
    }
  };

  const handleAddCategory = () => {
    if (!newCategory.trim()) return;
    if (checklist[newCategory]) {
      toast.warning("Category already exists!");
      return;
    }
    setChecklist({ ...checklist, [newCategory]: [] });
    setNewCategory("");
  };

  const handleDeleteCategory = (category) => {
    if (window.confirm(`Delete category "${category}"?`)) {
      const updated = { ...checklist };
      delete updated[category];
      setChecklist(updated);
    }
  };

  const handleAddQuestion = (category) => {
    const questionText = newQuestion[category];
    if (!questionText || !questionText.trim()) return;
    const updated = { ...checklist };
    updated[category] = [...updated[category], questionText];
    setChecklist(updated);
    setNewQuestion({ ...newQuestion, [category]: "" });
  };

  const handleDeleteQuestion = (category, index) => {
    const updated = { ...checklist };
    updated[category] = updated[category].filter((_, i) => i !== index);
    setChecklist(updated);
  };

  const handleQuestionChange = (category, index, value) => {
    const updated = { ...checklist };
    updated[category][index] = value;
    setChecklist(updated);
  };

  const handleSaveChecklist = async () => {
    const toastId = toast.loading("Saving checklist...");
    try {
      await fiveSService.saveChecklistMaster(checklist);
      toast.update(toastId, { render: "Checklist updated successfully!", type: "success", isLoading: false, autoClose: 3000, closeButton: true });
      setShowChecklistModal(false);
    } catch (error) {
      toast.update(toastId, { render: "Failed to save checklist.", type: "error", isLoading: false, autoClose: 3000, closeButton: true });
    }
  };

  return (
    <div className="pagecontainer fives-page">
      <div className="page-header">
        <div className="header-content">
          <h1>5S Audits</h1>
          <p>Sort, Set in order, Shine, Standardize, Sustain</p>
        </div>
        <div className="header-actions">
           <button className="btn-secondary btn" onClick={loadAudits}>
            <HiOutlineArrowPath /> Refresh
          </button>
          <button className="btn-secondary btn" onClick={openChecklistModal}>
            <HiOutlineQueueList /> Checklist Master
          </button>
          <button className="btn-primary btn" onClick={() => navigate("/home/fives-entry")}>
            <HiOutlinePlus /> New Audit
          </button>
        </div>
      </div>

      <div className="table-container">
        {loading ? (
          <PageLoading message="Loading audits…" />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Auditor</th>
                <th>Plant</th>
                <th>Area</th>
                <th className="text-center">Score</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {audits.map((audit) => (
                <tr key={audit.id}>
                  <td className="font-medium">
                    {audit.auditDate ? new Date(audit.auditDate).toLocaleDateString() : "-"}
                  </td>
                  <td>{audit.auditor}</td>
                  <td>{audit.plant?.plantName}</td>
                  <td>{audit.area?.areaName}</td>
                  <td className="text-center font-bold text-accent">{audit.totalScore}</td>
                  <td>
                    <div className="action-buttons flex justify-center">
                      <button className="btn-icon-edit" onClick={() => navigate(`/home/fives-entry/${audit.id}`)} title="Edit">
                        <HiOutlinePencil />
                      </button>
                      <button className="btn-icon-edit" onClick={() => generatePdf(audit)} title="Download PDF" style={{color: 'var(--status-info)'}}>
                        <HiOutlineDocumentArrowDown />
                      </button>
                      <button className="btn-icon-delete" onClick={() => handleDelete(audit.id)} title="Delete">
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {audits.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-tertiary">No audits found.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showChecklistModal}
        onClose={() => setShowChecklistModal(false)}
        title="Manage 5S Checklist Master"
        customWidth="900px"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <button className="btn-secondary btn" onClick={() => setShowChecklistModal(false)}>Cancel</button>
            <button className="btn-primary btn" onClick={handleSaveChecklist}><HiOutlineCheck /> Save Master</button>
          </div>
        }
      >
        <div className="shared-modal-body checklist-master-body">
          <div className="form-group mb-6">
            <label className="form-label">Create New Category</label>
            <div className="flex gap-2">
              <input type="text" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="E.g., Safety, Sorting..." className="flex-1" />
              <button className="btn-primary btn" onClick={handleAddCategory}><HiOutlinePlus /> Add</button>
            </div>
          </div>

          <div className="categories-list">
            {Object.keys(checklist).map((category) => (
              <div key={category} className="category-item-card">
                <div className="category-item-header">
                  <h3>{category}</h3>
                  <button className="btn-icon-delete" onClick={() => handleDeleteCategory(category)}><HiOutlineTrash /></button>
                </div>
                <div className="questions-container">
                  {checklist[category].map((question, index) => (
                    <div key={index} className="question-row">
                      <span className="q-index">{index + 1}.</span>
                      <input type="text" value={question} onChange={(e) => handleQuestionChange(category, index, e.target.value)} className="flex-1 q-input" />
                      <button className="btn-icon-delete btn-sm" onClick={() => handleDeleteQuestion(category, index)}><HiOutlineTrash /></button>
                    </div>
                  ))}
                  <div className="add-question-row mt-2">
                    <input
                      type="text"
                      value={newQuestion[category] || ""}
                      onChange={(e) => setNewQuestion({ ...newQuestion, [category]: e.target.value })}
                      placeholder="Type a new question and press Enter..."
                      className="flex-1 q-input ghost"
                      onKeyDown={(e) => e.key === "Enter" && handleAddQuestion(category)}
                    />
                    <button className="btn-icon-edit" onClick={() => handleAddQuestion(category)}><HiOutlinePlus /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SharedModal>
    </div>
  );
};

export default FiveS;
